import React, { useCallback, useEffect, useMemo } from 'react'
import { Button, SectionCard } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { useAppSelector } from '@store/hooks'
import {
  PRESETS_SCHEDULED_REPORTS_MOCK,
  type PresetsReturnState,
  type ScheduledReportRow,
  getPresetsPathWithTab,
} from '@constants/Reports/presetsAutomation.constants'

function ScheduledReportDetailPage(): React.ReactElement | null {
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useNotificationToast()
  const { id } = useParams<{ id: string }>()

  const fromRedux = useAppSelector((state) =>
    id ? state.reports.scheduledReports.find((r) => r.id === id) : undefined,
  )

  const scheduled: ScheduledReportRow | undefined = useMemo(() => {
    if (!id) return undefined
    return fromRedux ?? PRESETS_SCHEDULED_REPORTS_MOCK.find((r) => r.id === id)
  }, [id, fromRedux])

  useEffect(() => {
    if (id && !scheduled) {
      showToast(
        createFailedToast(
          'Schedule not found',
          'This scheduled report is no longer available.',
        ),
      )
      navigate(getPresetsPathWithTab('scheduledReports'), { replace: true })
    }
  }, [id, navigate, scheduled, showToast])

  const backToScheduledTab = useCallback(() => {
    const tab = (location.state as PresetsReturnState | null)?.presetsReturnTab
    navigate(
      tab
        ? getPresetsPathWithTab(tab)
        : getPresetsPathWithTab('scheduledReports'),
    )
  }, [location.state, navigate])

  if (!scheduled) return null

  return (
    <div className="reports-preview-shell">
      <div className="reports-scheduled-detail">
        <header className="reports-preview-header reports-scheduled-detail__header">
          <Button
            type="button"
            variant="text"
            className="reports-preview-header__title-btn"
            onClick={backToScheduledTab}
            leftIcon={<ArrowLeftIcon aria-hidden />}
          >
            <span>Scheduled report</span>
          </Button>
        </header>

        <div className="reports-scheduled-detail__body">
          <SectionCard radius="lg" className="reports-scheduled-detail__card">
            <h2 className="reports-scheduled-detail__report-title">
              {scheduled.name}
            </h2>
            <dl className="reports-scheduled-detail__meta">
              <div className="reports-scheduled-detail__meta-row">
                <dt>Schedule</dt>
                <dd>{scheduled.summaryLine}</dd>
              </div>
              <div className="reports-scheduled-detail__meta-row">
                <dt>Date saved</dt>
                <dd>{scheduled.dateSaved}</dd>
              </div>
              <div className="reports-scheduled-detail__meta-row">
                <dt>Last updated</dt>
                <dd>{scheduled.lastUpdated}</dd>
              </div>
              <div className="reports-scheduled-detail__meta-row">
                <dt>Saved by</dt>
                <dd>{scheduled.savedBy}</dd>
              </div>
              <div className="reports-scheduled-detail__meta-row">
                <dt>Scheduled</dt>
                <dd>{scheduled.scheduled ? 'Yes' : 'No'}</dd>
              </div>
              <div className="reports-scheduled-detail__meta-row">
                <dt>Schedule date</dt>
                <dd>{scheduled.scheduleDate}</dd>
              </div>
              {scheduled.dateAdded ? (
                <div className="reports-scheduled-detail__meta-row">
                  <dt>Added in app</dt>
                  <dd>{scheduled.dateAdded}</dd>
                </div>
              ) : null}
            </dl>
            <p className="reports-scheduled-detail__hint">
              Re-access this entry from the Presets page under Scheduled
              Reports.
            </p>
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

export default ScheduledReportDetailPage
